import { parseJobDate } from '../../utils/newDate';

/** Same ISO-instant shape as admin `dateValue` (BL-31). Legacy strings are not instants. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

const DEFAULT_JOB_TIMEZONE = 'America/Denver';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function jobTimezone(timezone: unknown): string {
    if (typeof timezone !== 'string' || !timezone.trim()) return DEFAULT_JOB_TIMEZONE;
    const zone = timezone.trim();
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone }).format();
        return zone;
    } catch {
        return DEFAULT_JOB_TIMEZONE;
    }
}

export type CalendarYmd = { year: number; month: number; day: number };

/** IANA zone from `timezone`, then `timeZone`, defaulting to America/Denver. */
export function resolveJobTimeZone(timezone?: unknown, timeZone?: unknown): string {
    return jobTimezone(timezone ?? timeZone);
}

function calendarYmd(date: Date, timezone: unknown): CalendarYmd | null {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: jobTimezone(timezone),
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        }).formatToParts(date);
        const num = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
        const year = num('year');
        const month = num('month');
        const day = num('day');
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;
        return { year, month, day };
    } catch {
        return null;
    }
}

function formatCalendarDay(ymd: CalendarYmd): string | null {
    const month = SHORT_MONTHS[ymd.month - 1];
    if (!month) return null;
    return `${month} ${ymd.day}, ${ymd.year}`;
}

/**
 * Calendar y/m/d for an ISO instant or a Timestamp-like value, in `timezone`,
 * or `timeZone` when `timezone` is missing, defaulting to America/Denver.
 * Legacy and other non-instant strings return null so the caller can keep a prefix.
 */
export function appointmentCalendarYmd(date: unknown, timezone?: unknown, timeZone?: unknown): CalendarYmd | null {
    try {
        const zone = timezone ?? timeZone;
        if (typeof date === 'string') {
            const value = date.trim();
            if (!ISO_INSTANT.test(value)) return null;
            const instant = new Date(value);
            if (Number.isNaN(instant.getTime())) return null;
            return calendarYmd(instant, zone);
        }
        const parsed = parseJobDate(date);
        if (!parsed || Number.isNaN(parsed.getTime())) return null;
        return calendarYmd(parsed, zone);
    } catch {
        return null;
    }
}

/**
 * Open-job date line.
 * An ISO instant, and a Timestamp-like value `parseJobDate` accepts (`toDate`, `{seconds}`, `{_seconds}`),
 * is the calendar day in `timezone`, or `timeZone` when `timezone` is missing, defaulting to America/Denver.
 * Legacy non-ISO strings keep their first ten characters.
 * Missing, blank, and non-dates are empty.
 */
export function openJobDateLabel(date: unknown, timezone?: unknown, timeZone?: unknown): string {
    try {
        if (typeof date === 'string') {
            const value = date.trim();
            if (!value) return '';
            const ymd = appointmentCalendarYmd(value, timezone, timeZone);
            if (!ymd) return value.slice(0, 10);
            return formatCalendarDay(ymd) ?? value.slice(0, 10);
        }
        const ymd = appointmentCalendarYmd(date, timezone, timeZone);
        return ymd ? formatCalendarDay(ymd) ?? '' : '';
    } catch {
        return '';
    }
}
